import { Handle, type AspectRatio, type Point, type Rect, type Size } from '@/core/geometry/types'
import { assertPositiveSize } from '@/core/geometry/validate'

function assertRatio(ratio: AspectRatio): void {
  if (!Number.isFinite(ratio.w) || !Number.isFinite(ratio.h) || ratio.w <= 0 || ratio.h <= 0) {
    throw new Error('Aspect ratio must have finite, positive sides')
  }
}

export function largestRectForRatio(bounds: Size, ratio: AspectRatio): Rect {
  assertPositiveSize(bounds, 'Bounds')
  assertRatio(ratio)

  const boundsWidth = Math.max(1, Math.floor(bounds.width))
  const boundsHeight = Math.max(1, Math.floor(bounds.height))
  const ratioValue = ratio.w / ratio.h
  let width: number
  let height: number

  if (boundsWidth / boundsHeight > ratioValue) {
    width = Math.max(1, Math.min(boundsWidth, Math.floor(boundsHeight * ratioValue)))
    height = Math.max(1, Math.min(boundsHeight, Math.round(width / ratioValue)))
  } else {
    width = boundsWidth
    height = Math.max(1, Math.min(boundsHeight, Math.round(width / ratioValue)))
  }

  return {
    x: Math.floor((boundsWidth - width) / 2),
    y: Math.floor((boundsHeight - height) / 2),
    width,
    height,
  }
}

export function clampRectInside(rect: Rect, bounds: Size): Rect {
  assertPositiveSize(rect, 'Rect')
  assertPositiveSize(bounds, 'Bounds')

  const boundsWidth = Math.max(1, Math.floor(bounds.width))
  const boundsHeight = Math.max(1, Math.floor(bounds.height))
  const width = Math.min(boundsWidth, Math.max(1, Math.round(rect.width)))
  const height = Math.min(boundsHeight, Math.max(1, Math.round(rect.height)))

  return {
    x: Math.min(boundsWidth - width, Math.max(0, Math.round(rect.x))),
    y: Math.min(boundsHeight - height, Math.max(0, Math.round(rect.y))),
    width,
    height,
  }
}

export function centeredPresetRect(current: Rect, bounds: Size, largest: Rect): Rect {
  const center = { x: current.x + current.width / 2, y: current.y + current.height / 2 }
  return clampRectInside({
    ...largest,
    x: center.x - largest.width / 2,
    y: center.y - largest.height / 2,
  }, bounds)
}

/** Preset change: largest rect of the new ratio inside the whole source, centered on the current crop. */
export function presetRectForRatio(current: Rect, bounds: Size, ratio: AspectRatio): Rect {
  return centeredPresetRect(current, bounds, largestRectForRatio(bounds, ratio))
}

export function integerRectInside(bounds: Rect): Rect {
  assertPositiveSize(bounds, 'Rect bounds')
  const left = Math.ceil(bounds.x)
  const top = Math.ceil(bounds.y)
  const right = Math.floor(bounds.x + bounds.width)
  const bottom = Math.floor(bounds.y + bounds.height)
  if (right <= left || bottom <= top) throw new Error('Rect bounds contain no positive integer-pixel region')
  return { x: left, y: top, width: right - left, height: bottom - top }
}

export function largestRectForRatioInside(bounds: Rect, ratio: AspectRatio): Rect {
  const region = integerRectInside(bounds)
  const local = largestRectForRatio(region, ratio)
  return { ...local, x: local.x + region.x, y: local.y + region.y }
}

export function centeredPresetRectInside(current: Rect, bounds: Rect, ratio: AspectRatio): Rect {
  const region = integerRectInside(bounds)
  const largest = largestRectForRatio(region, ratio)
  const localCurrent = { ...current, x: current.x - region.x, y: current.y - region.y }
  const local = centeredPresetRect(localCurrent, region, largest)
  return { ...local, x: local.x + region.x, y: local.y + region.y }
}

export function constrainRectToRatioInside(current: Rect, bounds: Rect, ratio: AspectRatio): Rect {
  const region = integerRectInside(bounds)
  const maximum = largestRectForRatio(region, ratio)
  const requestedBounds = {
    width: Math.min(Math.round(current.width), maximum.width),
    height: Math.min(Math.round(current.height), maximum.height),
  }
  const requested = largestRectForRatio(requestedBounds, ratio)
  const centered = {
    ...requested,
    x: current.x + current.width / 2 - requested.width / 2 - region.x,
    y: current.y + current.height / 2 - requested.height / 2 - region.y,
  }
  const local = clampRectInside(centered, region)
  return { ...local, x: local.x + region.x, y: local.y + region.y }
}

function desiredCornerWidth(rect: Rect, handle: Handle, delta: Point, ratioValue: number): number {
  const horizontalSign = handle === Handle.TopLeft || handle === Handle.BottomLeft ? -1 : 1
  const verticalSign = handle === Handle.TopLeft || handle === Handle.TopRight ? -1 : 1
  const horizontalWidth = rect.width + horizontalSign * delta.x
  const verticalWidth = rect.width + verticalSign * delta.y * ratioValue
  return Math.abs(horizontalWidth - rect.width) >= Math.abs(verticalWidth - rect.width)
    ? horizontalWidth
    : verticalWidth
}

function integerRatioSize(
  desiredWidth: number,
  maxWidth: number,
  maxHeight: number,
  ratioValue: number,
  minimumShortSide: number,
): Size {
  const maximumAllowedWidth = Math.max(1, Math.min(maxWidth, maxHeight * ratioValue))
  const requestedMinimumWidth = ratioValue >= 1
    ? minimumShortSide * ratioValue
    : minimumShortSide
  const minimumAllowedWidth = Math.min(maximumAllowedWidth, requestedMinimumWidth)
  const limitedWidth = Math.max(minimumAllowedWidth, Math.min(desiredWidth, maximumAllowedWidth))
  let height = Math.max(1, Math.floor(limitedWidth / ratioValue))
  let width = Math.max(1, Math.round(height * ratioValue))

  if (width > maxWidth) {
    width = Math.max(1, Math.floor(maxWidth))
    height = Math.max(1, Math.round(width / ratioValue))
  }
  if (height > maxHeight) {
    height = Math.max(1, Math.floor(maxHeight))
    width = Math.max(1, Math.round(height * ratioValue))
  }

  return { width, height }
}

export function resizeRectKeepingRatio(
  rect: Rect,
  handle: Handle,
  delta: Point,
  ratio: AspectRatio,
  bounds: Size,
  minimumShortSide = 1,
): Rect {
  assertPositiveSize(rect, 'Rect')
  assertPositiveSize(bounds, 'Bounds')
  assertRatio(ratio)
  if (!Number.isFinite(minimumShortSide) || minimumShortSide <= 0) {
    throw new Error('Minimum resize side must be finite and positive')
  }

  const ratioValue = ratio.w / ratio.h
  const centerX = rect.x + rect.width / 2
  const centerY = rect.y + rect.height / 2
  const right = rect.x + rect.width
  const bottom = rect.y + rect.height
  let desiredWidth: number
  let maxWidth: number
  let maxHeight: number
  let x: number
  let y: number

  switch (handle) {
    case Handle.TopLeft:
    case Handle.TopRight:
    case Handle.BottomRight:
    case Handle.BottomLeft: {
      desiredWidth = desiredCornerWidth(rect, handle, delta, ratioValue)
      const growsLeft = handle === Handle.TopLeft || handle === Handle.BottomLeft
      const growsUp = handle === Handle.TopLeft || handle === Handle.TopRight
      const anchorX = growsLeft ? right : rect.x
      const anchorY = growsUp ? bottom : rect.y
      maxWidth = growsLeft ? anchorX : bounds.width - anchorX
      maxHeight = growsUp ? anchorY : bounds.height - anchorY
      const size = integerRatioSize(desiredWidth, maxWidth, maxHeight, ratioValue, minimumShortSide)
      x = growsLeft ? anchorX - size.width : anchorX
      y = growsUp ? anchorY - size.height : anchorY
      return clampRectInside({ x, y, ...size }, bounds)
    }
    case Handle.Left:
    case Handle.Right: {
      const growsLeft = handle === Handle.Left
      desiredWidth = rect.width + (growsLeft ? -delta.x : delta.x)
      const anchorX = growsLeft ? right : rect.x
      maxWidth = growsLeft ? anchorX : bounds.width - anchorX
      maxHeight = 2 * Math.min(centerY, bounds.height - centerY)
      const size = integerRatioSize(desiredWidth, maxWidth, maxHeight, ratioValue, minimumShortSide)
      x = growsLeft ? anchorX - size.width : anchorX
      y = centerY - size.height / 2
      return clampRectInside({ x, y, ...size }, bounds)
    }
    case Handle.Top:
    case Handle.Bottom: {
      const growsUp = handle === Handle.Top
      const desiredHeight = rect.height + (growsUp ? -delta.y : delta.y)
      desiredWidth = desiredHeight * ratioValue
      const anchorY = growsUp ? bottom : rect.y
      maxWidth = 2 * Math.min(centerX, bounds.width - centerX)
      maxHeight = growsUp ? anchorY : bounds.height - anchorY
      const size = integerRatioSize(desiredWidth, maxWidth, maxHeight, ratioValue, minimumShortSide)
      x = centerX - size.width / 2
      y = growsUp ? anchorY - size.height : anchorY
      return clampRectInside({ x, y, ...size }, bounds)
    }
  }
}
